<template>
  <ViewContainer :title="$t('explore.explore')">
    <v-card class="ma-0 pa-0 pb-6 pt-6">
      <ExploreFilters
        class="ml-10 mr-10 mb-3"
        @change="handleChangeNewFilters"
      />
      <div
        v-if="!loading"
        class="mb-4 ml-10 mr-10 d-flex align-center justify-space-between"
      >
        <p
          class="caption blue-grey--text text--darken-3 mb-0"
          data-testid="explore-label-totalItems"
        >
          {{
            $t('common.resultWithCount', {
              count: totalItems,
            })
          }}
        </p>
        <div>
          <div class="d-flex">
            <ColumnsSelector
              v-if="isTableViewSelected"
              persistenceKey="exploreTable"
              button-class="mr-3"
              data-testid="explore-button-columnsSelector"
              :headers="defaultHeaders"
              @toggleColumn="onToggleColumn"
              @setInitialColumns="setInitialColumns"
            />

            <Menu
              v-if="!isTableViewSelected"
              :closeOnContentClick="false"
              :options="sortOptions"
              :selectedOption="sortSelected"
            >
              <template v-slot:activator="{ on }">
                <v-btn
                  v-on="on"
                  text
                  color="white"
                  class="mx-0 px-0 mr-5"
                  data-testid="explore-button-toggleSort"
                  data-tourid="explore-button-toggleSort"
                  :disabled="!totalItems || loading"
                  :style="totalItems && !loading ? '' : 'opacity : 0.5'"
                >
                  <v-icon color="blue-grey darken-1" left>mdi-sort</v-icon>
                  <span class="blue-grey--text text--darken-1 caption">
                    {{ $t('directory.sort') }}: {{ selectedSortLabel }}
                  </span>
                  <v-icon color="blue-grey darken-1" right
                    >mdi-menu-down</v-icon
                  >
                </v-btn>
              </template>
            </Menu>

            <Menu
              :closeOnContentClick="false"
              :options="viewOptions"
              :selectedOption="viewType"
            >
              <template v-slot:activator="{ on }">
                <v-btn
                  v-on="on"
                  text
                  color="white"
                  class="ms-0 px-0 ml-5"
                  data-testid="explore-button-toggleView"
                  data-tourid="explore-button-toggleView"
                  :disabled="!totalItems || loading"
                  :style="totalItems && !loading ? '' : 'opacity : 0.5'"
                >
                  <v-icon color="blue-grey darken-1">mdi-eye-outline</v-icon>
                  <span class="pl-3 caption blue-grey--text text--darken-1">{{
                    $t('common.view')
                  }}</span>

                  <v-icon color="blue-grey darken-1" right
                    >mdi-menu-down</v-icon
                  >
                </v-btn>
              </template>
            </Menu>
          </div>
        </div>
      </div>
      <ExploreContent
        data-tourid="explore-results"
        class="ml-7 mr-7"
        :searchValue="searchValue"
        :isTableView="isTableViewSelected"
        :currentPage="currentPage"
        :itemsPerPage="itemsPerPage"
        :sort="sortSelected"
        :filters="parsedFilters"
        :hasFiltersSelected="hasFiltersSelected"
        :selectedHeaders="selectedHeaders"
        @state="loading = $event"
        @sort="onSortSelected"
        @change="handleContentChange"
      />
    </v-card>

    <v-pagination
      v-if="shouldDisplayPagination"
      circle
      class="my-4"
      total-visible="10"
      data-testid="explore-pagination"
      :length="pages"
      :value="currentPage"
      @input="handleChangeCurrentPage"
    />
  </ViewContainer>
</template>

<script>
import _ from 'lodash'
import { mapGetters } from 'vuex'

import mixin from '@/mixins/mixin.vue'
import trackerMixin from '@/mixins/tracking/trackerMixin.vue'

import ViewContainer from '@/components/Core/Layout/ViewContainer.vue'
import ExploreFilters from '@/containers/Explore/ExploreFilters.vue'
import ExploreContent from '@/containers/Explore/ExploreContent.vue'
import Menu from '@/components/UI/Menu.vue'
import ColumnsSelector from '@/components/ColumnsSelector.vue'

const SORT_OPTIONS = {
  coincidence: '_score-desc',
  newestFirst: 'createdAt-desc',
  oldestFirst: 'createdAt-asc',
  lastUpdated: 'updatedAt-desc',
  highestPriority: 'priorityScore-desc',
  alphabeticalAsc: 'name-asc',
  alphabeticalDesc: 'name-desc',
  predictedFinancialImpactInUSD: 'predictedFinancialImpactInUSD-desc',
}
const VIEW_OPTIONS = {
  cards: 'cards',
  table: 'table',
}
const ITEMS_PER_PAGE_TABLE = 20
const ITEMS_PER_PAGE_CARDS = 24
const MAX_PAGES = 500 // This is necessary because stream can't paginate more of 10,000 rows

export default {
  name: 'ExploreView',

  mixins: [mixin, trackerMixin],

  components: {
    ViewContainer,
    ExploreFilters,
    ExploreContent,
    Menu,
    ColumnsSelector,
  },

  created() {
    this.viewType = this.$route.query.view ?? 'cards'
    this.isTableViewSelected = this.viewType === VIEW_OPTIONS.table
    this.sortSelected = this.$route.query.sort ?? this.defaultSortOption
    this.autoSelectPredictedFinancialImpactColumn()
  },

  data() {
    return {
      showFilters: true,
      searchValue: '',
      totalItems: 0,
      currentPage: 1,
      itemsPerPage: ITEMS_PER_PAGE_CARDS,
      sortSelected: SORT_OPTIONS.coincidence,
      loading: false,
      viewType: 'cards',
      isTableViewSelected: false,
      filters: {},
      defaultHeaders: _.compact([
        {
          text: this.$t('common.name'),
          cellClass: 'cursor-pointer',
          value: 'name',
          sortable: true,
          width: 234,
          show: true,
        },
        {
          text: this.$t('common.type'),
          cellClass: 'cursor-pointer',
          value: 'type',
          sortable: false,
          show: true,
        },
        {
          text: this.$t('common.status'),
          cellClass: 'cursor-pointer',
          value: 'status',
          sortable: true,
          show: true,
        },
        {
          text: this.$t('common.owner'),
          cellClass: 'cursor-pointer',
          value: 'createdBy',
          sortable: false,
          show: true,
        },
        {
          text: this.$t('common.roles.coach_one'),
          cellClass: 'cursor-pointer',
          value: 'coach',
          sortable: false,
          show: true,
        },
        {
          text: this.$t('common.tag_other'),
          cellClass: 'cursor-pointer',
          value: 'tags',
          sortable: true,
          show: true,
        },
        {
          text: this.$t('explore.fields.totalLikes'),
          cellClass: 'cursor-pointer',
          value: 'totalLikes',
          sortable: true,
          show: false,
        },
        {
          text: this.$t('routines.routine_one'),
          cellClass: 'cursor-pointer',
          value: 'routine',
          sortable: false,
          width: 180,
          show: false,
        },
        {
          text: this.$t('common.assignee_other'),
          cellClass: 'cursor-pointer',
          value: 'assignees',
          sortable: true,
          show: false,
        },
        {
          text: this.$t('filters.date.creation_date'),
          cellClass: 'cursor-pointer',
          value: 'createdAt',
          sortable: true,
          show: false,
        },
        {
          text: this.$t('filters.date.modified_date'),
          cellClass: 'cursor-pointer',
          value: 'updatedAt',
          sortable: true,
          show: false,
        },
        {
          text: this.$t('explore.fields.predicted_financial_impact_in_usd'),
          cellClass: 'cursor-pointer',
          value: 'predictedFinancialImpactInUSD',
          sortable: false,
          show: false,
        },
      ]),
    }
  },

  watch: {
    '$route.query.search'(currentValue) {
      this.searchValue = _.isArray(currentValue)
        ? currentValue[0]
        : currentValue
      this.sortSelected = this.defaultSortOption
      this.currentPage = 1
    },
    '$route.query.view'(currentValue) {
      this.viewType = currentValue
      this.isTableViewSelected = currentValue === VIEW_OPTIONS.table
    },
    '$route.query.sort'(currentValue) {
      this.sortSelected = _.isArray(currentValue)
        ? currentValue[0]
        : currentValue
    },
    isTableViewSelected(currentValue) {
      this.itemsPerPage = currentValue
        ? ITEMS_PER_PAGE_TABLE
        : ITEMS_PER_PAGE_CARDS
    },
    sortSelected(currentValue) {
      if (!currentValue?.startsWith('predictedFinancialImpactInUSD')) return

      this.autoSelectPredictedFinancialImpactColumn()
    },
  },

  computed: {
    ...mapGetters(['organization']),

    hasRoutinesFeatureEnabled() {
      return Boolean(this.organization?.featuresEnabled__routines)
    },

    isTasksEnabled() {
      return Boolean(this.organization?.featuresEnabled__tasks)
    },

    isSortedByPredictedFinancialImpact() {
      return this.sortSelected?.startsWith('predictedFinancialImpactInUSD')
    },

    selectedHeaders() {
      return this.defaultHeaders.filter(header => header.show === true)
    },

    pages() {
      const totalPages = Math.ceil(this.totalItems / this.itemsPerPage) || 0
      if (totalPages > MAX_PAGES) return MAX_PAGES
      return totalPages
    },

    shouldDisplayPagination() {
      return this.pages > 1 && !this.loading && !this.isTableViewSelected
    },

    activeFiltersCount() {
      return Object.keys(this.filters)?.filter(filter => {
        const isArrayOrObject =
          _.isObject(this.filters?.[filter]) ||
          _.isArray(this.filters?.[filter])
        return isArrayOrObject
          ? !_.isEmpty(this.filters?.[filter])
          : !!this.filters?.[filter]
      })?.length
    },

    canSortByCoincidence() {
      return !_.isEmpty(this.searchValue)
    },

    selectedSortLabel() {
      const selected = _.find(this.sortOptions, { id: this.sortSelected })
      return selected?.title ?? ''
    },

    defaultSortOption() {
      return this.canSortByCoincidence
        ? SORT_OPTIONS.coincidence
        : SORT_OPTIONS.newestFirst
    },

    viewOptions() {
      return [
        {
          title: this.$t('common.view_as_cards'),
          id: VIEW_OPTIONS.cards,
          testId: 'explore-viewOption-cards',
          action: () => this.onViewSelected(VIEW_OPTIONS.cards),
        },
        {
          title: this.$t('common.view_as_list'),
          id: VIEW_OPTIONS.table,
          testId: 'explore-viewOption-list',
          action: () => this.onViewSelected(VIEW_OPTIONS.table),
        },
      ]
    },

    sortOptions() {
      return _.compact([
        this.canSortByCoincidence && {
          title: this.$t('common.coincidence'),
          id: SORT_OPTIONS.coincidence,
          testId: 'explore-sortOptions-coincidence',
          action: () => this.onSortSelected(SORT_OPTIONS.coincidence),
        },
        {
          title: this.$t('filters.date.creation_date_newest_first'),
          id: SORT_OPTIONS.newestFirst,
          testId: 'explore-sortOptions-newestFirst',
          action: () => this.onSortSelected(SORT_OPTIONS.newestFirst),
        },
        {
          title: this.$t('filters.date.creation_date_oldest_first'),
          id: SORT_OPTIONS.oldestFirst,
          testId: 'explore-sortOptions-oldestFirst',
          action: () => this.onSortSelected(SORT_OPTIONS.oldestFirst),
        },
        {
          title: this.$t('filters.date.updated_date_newest_first'),
          id: SORT_OPTIONS.lastUpdated,
          testId: 'explore-sortOptions-lastUpdated',
          action: () => this.onSortSelected(SORT_OPTIONS.lastUpdated),
        },
        {
          title: this.$t('filters.date.highest_priority'),
          id: SORT_OPTIONS.highestPriority,
          testId: 'explore-sortOptions-highestPriority',
          action: () => this.onSortSelected(SORT_OPTIONS.highestPriority),
        },
        {
          title: this.$t('explore.highest_potential_impact'),
          id: SORT_OPTIONS.predictedFinancialImpactInUSD,
          testId: 'explore-sortOptions-predictedFinancialImpact',
          action: () =>
            this.onSortSelected(SORT_OPTIONS.predictedFinancialImpactInUSD),
        },
        {
          title: this.$t('common.a_z'),
          id: SORT_OPTIONS.alphabeticalAsc,
          testId: 'explore-sortOptions-alphabeticalAsc',
          action: () => this.onSortSelected(SORT_OPTIONS.alphabeticalAsc),
        },
        {
          title: this.$t('common.z_a'),
          id: SORT_OPTIONS.alphabeticalDesc,
          testId: 'explore-sortOptions-alphabeticalDesc',
          action: () => this.onSortSelected(SORT_OPTIONS.alphabeticalDesc),
        },
      ])
    },

    parsedFilters() {
      const currentFilters = _.cloneDeep(this.filters)

      const disabledEntities = new Set()

      if (!this.hasRoutinesFeatureEnabled) {
        disabledEntities.add(this.$constants.exploreEntityTypes.routine)
        disabledEntities.add(this.$constants.exploreEntityTypes.finding)
      }

      if (!this.isTasksEnabled) {
        disabledEntities.add(this.$constants.exploreEntityTypes.task)
      }

      if (disabledEntities.size) {
        const allTypes = Object.values(this.$constants.exploreEntityTypes)

        const selectedTypesExceptDisabled = (
          currentFilters?.types ?? []
        ).filter(entity => !disabledEntities.has(entity))
        const allTypesExceptDisabled = allTypes.filter(
          entity => !disabledEntities.has(entity)
        )

        currentFilters.types = selectedTypesExceptDisabled.length
          ? selectedTypesExceptDisabled
          : allTypesExceptDisabled
      }

      currentFilters.createdAtOption = undefined
      currentFilters.dueDateOption = undefined
      currentFilters.createdAtOption = undefined

      return currentFilters
    },

    hasFiltersSelected() {
      return Object.keys(this.filters).some(key => {
        const isObjectOrArray =
          _.isObject(this.filters[key]) || _.isArray(this.filters[key])
        return isObjectOrArray
          ? !_.isEmpty(this.filters[key])
          : this.filters[key]
      })
    },
  },

  methods: {
    handleChangeCurrentPage(nextPage) {
      this.currentPage = nextPage
      this.trackExplorePaginate({
        page: nextPage,
        view: this.isTableViewSelected
          ? VIEW_OPTIONS.table
          : VIEW_OPTIONS.cards,
      })
      this.updateRoute({ page: this.currentPage })
    },

    onViewSelected(option) {
      this.isTableViewSelected = option === VIEW_OPTIONS.table
      this.trackExploreView({
        type: option,
      })
      this.updateRoute({ view: option })
    },

    onSortSelected(option) {
      this.sortSelected = option
      this.autoSelectPredictedFinancialImpactColumn()
      this.trackExploreView({
        sort: option,
      })
      this.updateRoute({ sort: this.sortSelected })
    },

    handleContentChange(event) {
      this.totalItems = event.totalItems
      this.currentPage = event.currentPage
      this.itemsPerPage = event.itemsPerPage
      this.updateRoute({ page: this.currentPage })
    },

    handleToggleFilters() {
      this.showFilters = !this.showFilters
      this.trackExploreFilter({
        action: 'toggle',
        filters: this.filters,
      })
    },

    handleChangeFilters: _.debounce(function(newFilters) {
      this.filters = { ...newFilters }
      this.currentPage = 1
      this.updateRoute(newFilters)
    }, 500),

    handleChangeNewFilters: _.debounce(function(newFilters) {
      this.filters = { ...newFilters }
      this.currentPage = 1
    }, 500),

    async updateRoute(queryOption) {
      const newQuery = _.omitBy(
        {
          ...this.$route.query,
          ...queryOption,
        },
        _.isNull
      )
      await this.$router
        .replace({
          path: 'explore',
          query: newQuery,
        })
        .catch(() => {
          // This is intentional to avoid navigation duplicated error
        })
    },

    getQueryConditionFilter(filter = '') {
      return this.$route.query[filter] ?? undefined
    },

    getQueryFilter(filter = '', defaultValue = []) {
      return this.$route.query[filter] ?? defaultValue
    },

    autoSelectPredictedFinancialImpactColumn() {
      if (!this.sortSelected?.startsWith('predictedFinancialImpactInUSD'))
        return

      const index = this.defaultHeaders.findIndex(
        header => header.value === 'predictedFinancialImpactInUSD'
      )

      if (index >= 0 && !this.defaultHeaders[index].show) {
        this.defaultHeaders[index].show = true
      }
    },

    onToggleColumn(event) {
      this.defaultHeaders[event.index].show = event.value
    },

    setInitialColumns(selectedColumns) {
      this.defaultHeaders = this.getInitialSelectedTableColumns(
        this.defaultHeaders,
        selectedColumns
      )
    },
  },
}
</script>
